import type { CategoryIconKey, HelpCategory } from '@/content/help-centre/categories';
import type { HelpArticleMeta } from '@/pages/helpCentre/content/articleTypes';

export interface HelpMenuItem {
  label: string;
  to: string;
}

export interface HelpMenuSection {
  icon: string;
  label: string;
  to: string;
  items: HelpMenuItem[];
}

/**
 * The design's mega-menu uses MDI font icons; the help centre's category
 * metadata uses abstract icon keys. Map key → mdi class here so the menu
 * tracks whatever categories the help centre defines.
 */
const CATEGORY_MENU_ICONS: Record<CategoryIconKey, string> = {
  rocket: 'mdi-rocket-launch-outline',
  calendar: 'mdi-calendar-outline',
  members: 'mdi-account-group-outline',
  wallet: 'mdi-wallet-outline',
  chat: 'mdi-message-badge-outline',
  trophy: 'mdi-trophy-outline',
  megaphone: 'mdi-bullhorn-outline',
};

/**
 * Build the mega-menu sections from the help centre's real content: one
 * section per category that has published articles, each item linking to a
 * live article route. Deriving from the article index (rather than the
 * mockup's hand-written taxonomy) means the menu cannot drift from the
 * content that actually exists.
 */
export const buildHelpMenuSections = (
  categories: readonly HelpCategory[],
  articles: readonly HelpArticleMeta[],
): HelpMenuSection[] =>
  [...categories]
    .sort((a, b) => a.order - b.order)
    .map((category) => ({
      icon: CATEGORY_MENU_ICONS[category.icon],
      label: category.label,
      to: '/help-centre',
      items: articles
        .filter((article) => article.categorySlug === category.slug)
        .map((article) => ({ label: article.title, to: `/help-centre/${article.slug}` })),
    }))
    .filter((section) => section.items.length > 0);

/**
 * Load the sections. The article index eagerly inlines every article's raw
 * markdown, which route-level lazy loading deliberately keeps out of the
 * main bundle — so the menu imports it dynamically (on first hover intent),
 * landing it in a chunk shared with the help centre routes instead.
 */
let cache: Promise<HelpMenuSection[]> | undefined;

export const loadHelpMenuSections = (): Promise<HelpMenuSection[]> => {
  cache ??= Promise.all([
    import('@/content/help-centre/categories'),
    import('@/pages/helpCentre/content/articleIndex'),
  ])
    .then(([{ HELP_CATEGORIES }, { ALL_ARTICLES }]) =>
      buildHelpMenuSections(HELP_CATEGORIES, ALL_ARTICLES),
    )
    .catch((error: unknown) => {
      // Never cache a rejection — the next hover retries the import.
      cache = undefined;
      throw error;
    });
  return cache;
};
